import {useCallback, useEffect, useState, useRef} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {formDataLayerHelper} from 'utils/dataLayerHelper';

const MultiRangeSlider = ({settings, onChange}) => {
  const intl = useIntl();
  const {name, min, max, state, label, type, fromTo, disableMin = false, currentMax} = settings || {};
  const [minVal, setMinVal] = useState(min);
  const [maxVal, setMaxVal] = useState(max);
  const [initialMaxVal, setInitialMaxVal] = useState(currentMax);
  const minValRef = useRef(min);
  const maxValRef = useRef(max);
  const range = useRef(null);
  const arrowRight = useRef(null);
  const arrowLeft = useRef(null);
  const fromInput = useRef(null);
  const toInput = useRef(null);
  const dataLayerCallback = formDataLayerHelper(intl, settings);

  // Convert to percentage
  const getPercent = useCallback(
    (value) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  // Set width of the range to decrease from the left side
  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxValRef.current);

    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }

    if (arrowRight.current) {
      arrowRight.current.style.left = `calc(${minPercent}% + 11px)`;
    }
  }, [minVal, getPercent, state]);

  // Set width of the range to decrease from the right side
  useEffect(() => {
    const minPercent = getPercent(minValRef.current || 0);
    const maxPercent = getPercent(maxVal);

    if (range.current) {
      range.current.style.width = `${maxPercent - minPercent}%`;
    }

    if (arrowLeft.current) {
      arrowLeft.current.style.left = `calc(${maxPercent}% - 20px)`;
    }
  }, [maxVal, getPercent, state]);

  useEffect(() => {
    if (state[name].to !== maxVal) {
      setMaxVal(state[name].to);
    }
  }, [state]);

  // Get min and max values when their state changes
  useEffect(() => {
    if (initialMaxVal) {
      onChange({
        target: {
          name,
          type,
          value: {
            min: minVal,
            max: initialMaxVal,
          }
        }
      });
      if (!disableMin) {
        fromInput.current.value = `$${intl.formatNumber(minVal)}`;
      }
      toInput.current.value = `$${intl.formatNumber(initialMaxVal)}`;
      setInitialMaxVal(false);
    }
    else {
      onChange({
        target: {
          name,
          type,
          value: {
            min: minVal,
            max: maxVal,
          }
        }
      });
      if (!disableMin) {
        fromInput.current.value = `$${intl.formatNumber(minVal)}`;
      }
      toInput.current.value = `$${intl.formatNumber(maxVal)}`;
    }
  }, [minVal, maxVal]);

  const inputChange = (event) => {
    const {type, keyCode} = event;
    if (type === 'keyup' && keyCode !== 13) {
      return;
    }

    let {name, value} = event.target;
    value = parseInt(value.replace(/\D/g, ''));

    if (name === 'price_range-from-input') {
      if (!value) {
        value = minVal;
      }
      if (value < min) {
        value = min;
      }
      if (value > (max - 1)) {
        value = max - 1;
      }

      fromInput.current.value = `$${intl.formatNumber(value)}`;
      setMinVal(value);
    }

    if (name === 'price_range-to-input') {
      if (!value) {
        value = maxVal;
      }
      if (value < (min + 1)) {
        value = min + 1;
      }
      if (value > max - 1) {
        value = max;
      }

      toInput.current.value = `$${intl.formatNumber(value)}`;
      setMaxVal(value);
    }
  };

  return (
    <div className="relative form-item form-item--range">
      {label && <label className="select-label" htmlFor={name}>{intl.formatMessage({id: label})}</label>}
      <div className="mb-30">{fromTo}</div>
      <div className="relative range-wrap">
        {!disableMin ? <div ref={arrowRight} className="arrow-right"></div> : ''}
        <div ref={arrowLeft} className="arrow-left"></div>
        {!disableMin ? <input
          name={`${name}-from`}
          type="range"
          min={min}
          max={max}
          value={state && state[name] ? state[name].from : min}
          onChange={(event) => {
            const value = Math.min(Number(event.target.value), maxVal - 1);
            setMinVal(value);
            minValRef.current = value;
            if (typeof dataLayerCallback === 'function') {
              dataLayerCallback(event);
            }
          }}
          className="thumb thumb--left"
          style={{ zIndex: minVal > max - 100 && "5" }}
        /> : ''}
        <input
          name={`${name}-to`}
          type="range"
          min={min}
          max={max}
          value={state && state[name] ? state[name].to : max}
          onChange={(event) => {
            const value = Math.max(Number(event.target.value), minVal + 1);
            setMaxVal(value);
            maxValRef.current = value;
            if (typeof dataLayerCallback === 'function') {
              dataLayerCallback(event);
            }
          }}
          className="thumb thumb--right"
        />
        <div className="slider">
          <div className="slider__track" />
          <div ref={range} className="slider__range" />
        </div>
      </div>
      <div className="flex gap-x-10 range-inputs">
        {!disableMin ? <input name={`${name}-from-input`}
                              type="text"
                              defaultValue={`$${intl.formatNumber(min)}`}
                              onBlur={inputChange}
                              onKeyUp={inputChange}
                              ref={fromInput} /> : ''}
        <input name={`${name}-to-input`}
               type="text"
               defaultValue={`$${intl.formatNumber(max)}`}
               onBlur={inputChange}
               onKeyUp={inputChange}
               ref={toInput} />
      </div>
    </div>
  );
};

MultiRangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired
};

export default MultiRangeSlider;
